import { useState } from "react";
import { Badge, Icon } from "../../ds/DS";
import type { ActivityKind, VActivity } from "../data";
import type { PageProps } from "../Portal";
import { activityDay } from "./util";
import { PageHead, ScopeNote } from "./parts";

const FILTERS: { id: "all" | ActivityKind; label: string }[] = [
  { id: "all", label: "All" },
  { id: "ticket", label: "Tickets" },
  { id: "release", label: "Releases & milestones" },
  { id: "task", label: "Tasks" },
  { id: "change", label: "Change requests" },
  { id: "file", label: "Files" },
  { id: "access", label: "Access" },
];

const time = (ms: number) =>
  new Date(ms).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

export function Activity({ isAdmin, activity, pid, setPid, projectName }: PageProps) {
  const [filter, setFilter] = useState<"all" | ActivityKind>("all");
  // The server never sends internal entries to clients; the filter is belt
  // and braces.
  const visible = activity.filter(
    (a) => (isAdmin || !a.internal) && (pid === "all" || a.projectId === pid),
  );
  const shown = visible.filter((a) => filter === "all" || a.kind === filter);

  const days: { label: string; rows: VActivity[] }[] = [];
  for (const a of shown) {
    const label = activityDay(a.at);
    const last = days[days.length - 1];
    if (last && last.label === label) last.rows.push(a);
    else days.push({ label, rows: [a] });
  }

  return (
    <div className="stack-24">
      <PageHead
        eyebrow="ACTIVITY"
        title="Activity"
        sub={
          isAdmin
            ? "Everything on this account, most recent first. Internal entries are hidden from the client."
            : "Everything that's happened across your work with us, most recent first."
        }
      />
      <ScopeNote pid={pid} setPid={setPid} projectName={projectName} />

      <div className="chips">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            className={"chip" + (filter === f.id ? " is-on" : "")}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
            <span className="chip-n mono">
              {f.id === "all" ? visible.length : visible.filter((a) => a.kind === f.id).length}
            </span>
          </button>
        ))}
      </div>

      {days.length === 0 ? (
        <div className="list-card">
          <div className="list-empty">Nothing of this kind yet.</div>
        </div>
      ) : (
        days.map((d) => (
          <div key={d.label} className="stack-8">
            <div className="day-label mono">{d.label}</div>
            <div className="list-card is-padded">
              {d.rows.map((a) => (
                <div key={a.id} className="act-line">
                  <span className="act-time mono">{time(a.at)}</span>
                  <span className="act-ico">
                    <Icon name={a.icon} size={14} />
                  </span>
                  <div className="act-main">
                    <div className="act-text">{a.text}</div>
                    <div className="act-meta mono">
                      {a.projectId ? projectName(a.projectId) : "Client account"} · {a.who}
                    </div>
                  </div>
                  {isAdmin && a.internal ? <Badge tone="warning">Internal</Badge> : <span />}
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
